import playerClient from "../middleware/playerClient";

export const PAYMENT_METHODS_ENDPOINT = "payment-methods";
export const BONUS_OFFERS_ENDPOINT = "bonus-offers";
export const DEPOSIT_ENDPOINT = "deposit";
export const DEPOSIT_INITIATE_ENDPOINT = "deposit/initiate";
export const BANK_ACCOUNTS_ENDPOINT = "bank-accounts";
export const UPI_ACCOUNTS_ENDPOINT = "upi-accounts";
export const WITHDRAW_ENDPOINT = "withdraw";

/**
 * List payment methods for the authenticated player.
 * @param {{ flow?: 'deposit' | 'withdrawal' | 'both' }} options
 */
export async function listPaymentMethods({ flow = "deposit" } = {}) {
  const response = await playerClient.get(PAYMENT_METHODS_ENDPOINT, {
    params: { flow },
  });
  return response.data;
}

/**
 * List active bonus offers shown on the deposit screen.
 */
export async function listBonusOffers() {
  const response = await playerClient.get(BONUS_OFFERS_ENDPOINT);
  return response.data;
}

/**
 * Submit a manual deposit request (IMPS/UPI) with the UTR transaction reference.
 * Creates a PENDING transaction that staff must approve before crediting balance.
 * @param {{
 *   paymentMethodId: string,
 *   amount: number | string,
 *   utrReference: string,
 *   bonusTemplateId?: string,
 *   screenshot?: File | null,
 * }} payload
 */
export async function submitDeposit({
  paymentMethodId,
  amount,
  utrReference,
  bonusTemplateId,
  screenshot,
} = {}) {
  const body = {
    payment_method_id: paymentMethodId,
    amount: Number(String(amount).replace(/,/g, "")),
    utr_reference: utrReference,
  };

  if (bonusTemplateId) {
    body.bonus_template_id = bonusTemplateId;
  }

  // the optional receipt file needs multipart/form-data
  if (screenshot) {
    const formData = new FormData();
    Object.entries(body).forEach(([key, value]) =>
      formData.append(key, value)
    );
    formData.append("screenshot", screenshot);
    const response = await playerClient.post(DEPOSIT_ENDPOINT, formData, {
      headers: { "Content-Type": undefined },
    });
    return response.data;
  }

  const response = await playerClient.post(DEPOSIT_ENDPOINT, body);
  return response.data;
}

/**
 * Record a deposit intent (iDeposits) when the deposit screen is opened, and
 * again when the amount or payment method changes. The server reuses the
 * player's open intent from the last 24 hours, so repeat calls are safe.
 * @param {{ amount: number | string, paymentMethodId: string }} payload
 */
export async function initiateDeposit({ amount, paymentMethodId } = {}) {
  const response = await playerClient.post(DEPOSIT_INITIATE_ENDPOINT, {
    amount: Number(String(amount).replace(/,/g, "")),
    payment_method_id: paymentMethodId,
  });
  return response.data;
}

/**
 * Record why the player dropped out of the deposit flow (iDeposits).
 * @param {{
 *   intentId: string,
 *   reason: string,
 *   amount?: number | string,
 *   paymentMethodId?: string,
 * }} payload
 */
export async function abandonDeposit({
  intentId,
  reason,
  amount,
  paymentMethodId,
} = {}) {
  const body = { reason };
  if (amount !== undefined && amount !== "") {
    body.amount = Number(String(amount).replace(/,/g, ""));
  }
  if (paymentMethodId) {
    body.payment_method_id = paymentMethodId;
  }

  const response = await playerClient.patch(
    `${DEPOSIT_INITIATE_ENDPOINT}/${intentId}`,
    body
  );
  return response.data;
}

/**
 * Cancel one of the player's own deposit requests that is still pending.
 * Nothing was credited yet, so only the status changes.
 * @param {string} transactionId
 */
export async function cancelDeposit(transactionId) {
  const response = await playerClient.post(
    `${DEPOSIT_ENDPOINT}/${transactionId}/cancel`
  );
  return response.data;
}

/**
 * List saved bank accounts for withdrawals.
 */
export async function listBankAccounts() {
  const response = await playerClient.get(BANK_ACCOUNTS_ENDPOINT);
  return response.data;
}

/** POST api/v1/player/withdraw/{id}/cancel — the held amount returns to the wallet. */
export async function cancelWithdrawalRequest(id) {
  const response = await playerClient.post(`${WITHDRAW_ENDPOINT}/${id}/cancel`);
  return response.data;
}

/** POST api/v1/player/deposit/{id}/cancel */
export async function cancelDepositRequest(id) {
  const response = await playerClient.post(`${DEPOSIT_ENDPOINT}/${id}/cancel`);
  return response.data;
}

/**
 * Set a bank account as primary for withdrawals.
 * @param {string} bankAccountId
 */
export async function setPrimaryBankAccount(bankAccountId) {
  const response = await playerClient.patch(
    `${BANK_ACCOUNTS_ENDPOINT}/${bankAccountId}/primary`
  );
  return response.data;
}

/**
 * Remove a saved bank account.
 * @param {string} bankAccountId
 */
export async function removeBankAccount(bankAccountId) {
  const response = await playerClient.delete(
    `${BANK_ACCOUNTS_ENDPOINT}/${bankAccountId}`
  );
  return response.data;
}

/**
 * Remove a saved UPI account.
 * Uses the same bank-accounts DELETE endpoint as bank removal.
 * @param {string} upiAccountId
 */
export async function removeUpiAccount(upiAccountId) {
  const response = await playerClient.delete(
    `${BANK_ACCOUNTS_ENDPOINT}/${upiAccountId}`
  );
  return response.data;
}

/**
 * Add a bank account for withdrawals.
 * @param {{
 *   account_holder_name: string,
 *   account_number: string,
 *   ifsc_code: string,
 *   bank_name: string,
 *   account_type?: 'savings' | 'current',
 * }} payload
 */
export async function addBankAccount({
  account_holder_name,
  account_number,
  ifsc_code,
  bank_name,
  account_type = "savings",
}) {
  const response = await playerClient.post(BANK_ACCOUNTS_ENDPOINT, {
    account_holder_name,
    account_number,
    ifsc_code,
    bank_name,
    account_type,
  });
  return response.data;
}

/**
 * List saved UPI accounts for withdrawals.
 */
export async function listUpiAccounts() {
  const response = await playerClient.get(UPI_ACCOUNTS_ENDPOINT);
  return response.data;
}

/**
 * Add a UPI account for withdrawals (multipart/form-data).
 * @param {{
 *   upi_id: string,
 *   account_name?: string,
 *   account_holder_name?: string,
 *   qr_code_url?: string,
 *   file?: File | null,
 * }} payload
 */
export async function addUpiAccount({
  upi_id,
  account_name,
  account_holder_name,
  qr_code_url,
  file,
} = {}) {
  const formData = new FormData();
  formData.append("upi_id", upi_id);

  if (account_name) {
    formData.append("account_name", account_name);
  }
  if (account_holder_name) {
    formData.append("account_holder_name", account_holder_name);
  }
  if (qr_code_url) {
    formData.append("qr_code_url", qr_code_url);
  }
  if (file) {
    formData.append("file", file);
  }

  const response = await playerClient.post(UPI_ACCOUNTS_ENDPOINT, formData, {
    headers: {
      "Content-Type": undefined,
    },
  });
  return response.data;
}

/**
 * Submit a withdrawal request to a verified saved bank account.
 * Balance is deducted immediately; staff must approve for actual payout.
 * @param {{
 *   bankAccountId: string,
 *   amount: number | string,
 * }} payload
 */
export async function submitWithdraw({ bankAccountId, amount } = {}) {
  const response = await playerClient.post(WITHDRAW_ENDPOINT, {
    bank_account_id: bankAccountId,
    amount: Number(String(amount).replace(/,/g, "")),
  });
  return response.data;
}
